import { useState, useMemo } from 'react';
import { useLiffWithMember } from '../../hooks/useLiffWithMember';
import { useTenant } from '../../contexts/TenantContext';
import { getTenantIdFromUrl } from '../../api/apiUtils';
import { submitSurvey } from '../../api/surveyClient';
import { DynamicSurveyForm } from './components/DynamicSurveyForm';
import { Loading } from '../../components/Loading';
import { LiffLoginGate } from '../../components/LiffLoginGate';
import { useUserDetail } from '../../hooks/queries';
import { type SurveyTemplate } from '../../api/surveyTemplateClient';

function getLoginRedirectUri(): string {
  return (
    window.location.origin + window.location.pathname + window.location.search
  );
}

/**
 * 問卷調查應用程式
 * LIFF 問卷調查應用程式
 */
export function SurveyApp() {
  const {
    isReady,
    profile,
    error: liffError,
    liff,
    shouldUseLiff,
    isLoggedIn,
    ensureMember: ensureData,
    isMemberLoading: isLoadingEnsure,
    ensureError,
  } = useLiffWithMember();
  const { tenantId, isReady: tenantReady, error: tenantError } = useTenant();
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState<
    Array<{ field: string; message: string }> | undefined
  >(undefined);
  const [template, setTemplate] = useState<SurveyTemplate | null>(null);

  const fallbackTenantId = tenantId || getTenantIdFromUrl();
  const isLiffReady = !shouldUseLiff || isReady;
  const isTenantReady = tenantReady || !!fallbackTenantId;

  const userId = ensureData?.id;

  // 取得使用者詳細資訊（用於取得已填寫之手機，選填；電話與生日改由會員中心填寫）
  const { user: userDetail, isLoading: isLoadingDetail } = useUserDetail(
    userId,
    { enabled: !!userId && !!tenantId && tenantReady }
  );

  // 從 DynamicSurveyForm 接收模板（用於顯示標題）
  const handleTemplateLoad = (loadedTemplate: SurveyTemplate) => {
    setTemplate(loadedTemplate);
  };

  // 計算主題 CSS Variables（必須在所有條件渲染之前調用 hooks）
  const themeStyles = useMemo(() => {
    if (!template?.config.theme) return {};

    const theme = template.config.theme;
    const fontFamilyMap = {
      fun: '"Comic Sans MS", "微軟正黑體", sans-serif',
      formal: '"Times New Roman", "新細明體", serif',
      modern: '"Helvetica Neue", "微軟正黑體", sans-serif',
    };

    return {
      fontFamily: theme.fontStyle ? fontFamilyMap[theme.fontStyle] : undefined,
    } as React.CSSProperties;
  }, [template?.config.theme]);

  const handleSubmit = async (formData: Record<string, unknown>) => {
    setSubmitStatus('loading');

    try {
      // memberId = customer UUID（登入即加會員）；phone 選填，優先使用會員中心已填
      const { templateId, ...responseData } = formData;
      if (!ensureData?.id) throw new Error('尚未取得會員資料，請稍後再試');

      await submitSurvey({
        templateId: templateId as number,
        memberId: ensureData.id,
        phone: userDetail?.phone || '',
        responseData: responseData as Record<string, unknown>,
        lineUserId: profile?.userId,
        displayName: profile?.displayName,
      });

      setSubmitStatus('success');

      // 提交成功後 2 秒關閉 LIFF 視窗
      setTimeout(() => {
        if (liff?.isInClient()) {
          liff.closeWindow();
        }
      }, 2000);
    } catch (error) {
      console.error('提交錯誤:', error);
      setSubmitStatus('error');
      const message =
        error instanceof Error ? error.message : '提交失敗，請稍後再試';
      setErrorMessage(message);
      // 提取錯誤詳情（如果有的話）
      const details = (error as { details?: unknown })?.details;
      setErrorDetails(Array.isArray(details) ? details : undefined);
    }
  };

  const gateError = liffError ?? ensureError ?? null;

  const formTitle =
    template?.config.meta?.title || template?.title || '問卷調查';
  const formEmoji = template?.config.theme?.emoji || '';

  const isLoading =
    !isLiffReady ||
    (!isTenantReady && !fallbackTenantId) ||
    (!!profile &&
      isLiffReady &&
      isTenantReady &&
      (isLoadingEnsure || isLoadingDetail));

  if (tenantError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-6xl text-center mb-4">❌</div>
          <h2 className="text-2xl font-bold text-red-600 mb-4 text-center">
            租戶初始化失敗
          </h2>
          <p className="text-gray-700 text-center">{tenantError.message}</p>
          <p className="text-sm text-gray-500 mt-4 text-center">
            請確認 URL 是否包含正確的 tenantId 參數
          </p>
        </div>
      </div>
    );
  }

  return (
    <LiffLoginGate
      isReady={isReady}
      shouldUseLiff={shouldUseLiff}
      isLoggedIn={isLoggedIn}
      error={
        gateError instanceof Error
          ? gateError
          : gateError
            ? new Error(String(gateError))
            : null
      }
      onLoginClick={() => liff?.login?.({ redirectUri: getLoginRedirectUri() })}
    >
      {isLoading ? (
        <Loading variant="fullscreenBranded" />
      ) : submitStatus === 'success' ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-blue-600 mb-2">
              提交成功！
            </h2>
            <p className="text-gray-600 mb-4">感謝您填寫問卷</p>
            {profile?.displayName && (
              <p className="text-sm text-gray-500">
                謝謝 {profile.displayName} 的參與 🙏
              </p>
            )}
          </div>
        </div>
      ) : (
        <div
          className="min-h-screen py-8 px-4"
          style={{
            background:
              template?.config.theme?.backgroundColor ||
              'linear-gradient(to bottom right, #667eea 0%, #764ba2 100%)',
            ...themeStyles,
          }}
        >
          <div className="max-w-2xl mx-auto">
            <div
              className="rounded-2xl shadow-2xl overflow-hidden"
              style={{
                backgroundColor: template?.config.theme?.cardColor || '#FFFFFF',
              }}
            >
              {/* 標題區塊 */}
              <div
                className="text-white p-6 text-center"
                style={{
                  background: template?.config.theme?.primaryColor
                    ? `linear-gradient(to right, ${template.config.theme.primaryColor}, ${template.config.theme.primaryColor})`
                    : 'linear-gradient(to right, #4ECDC4, #44A08D)',
                }}
              >
                <h1
                  className="text-3xl font-bold mb-2"
                  style={{
                    color: template?.config.theme?.textColor || '#FFFFFF',
                  }}
                >
                  {formEmoji} {formTitle}
                </h1>
                {template?.description && (
                  <p className="text-sm mb-2 opacity-90">
                    {template.description}
                  </p>
                )}
                {profile?.displayName && (
                  <p className="opacity-90">歡迎 {profile.displayName}</p>
                )}
                {!profile && <p className="opacity-90">歡迎參與問卷調查</p>}
              </div>

              {/* 表單內容 */}
              <div className="p-6">
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                    <p className="text-red-600 font-semibold mb-2">
                      ❌ 提交失敗
                    </p>
                    <div className="text-red-700 whitespace-pre-line mb-3">
                      {errorMessage}
                    </div>
                    {errorDetails && errorDetails.length > 0 && (
                      <div className="mt-3 space-y-1">
                        <p className="text-sm font-semibold text-red-600">
                          錯誤詳情：
                        </p>
                        <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                          {errorDetails.map((detail, index) => {
                            let fieldName = detail.field;
                            // 將字段名轉換為更友好的顯示名稱
                            if (fieldName === 'memberId')
                              fieldName = '會員手機號碼';
                            else if (fieldName === 'phone')
                              fieldName = '手機號碼';
                            return (
                              <li key={index}>
                                <span className="font-medium">{fieldName}</span>
                                ：{detail.message}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setSubmitStatus('idle');
                        setErrorMessage('');
                        setErrorDetails(undefined);
                      }}
                      className="mt-3 text-sm text-red-700 underline hover:text-red-900"
                    >
                      重新嘗試
                    </button>
                  </div>
                )}

                {/* 優先使用動態表單，如果載入失敗則使用固定表單 */}
                <DynamicSurveyForm
                  onSubmit={handleSubmit}
                  isSubmitting={submitStatus === 'loading'}
                  onTemplateLoad={handleTemplateLoad}
                />
              </div>
            </div>

            {/* 頁尾資訊 */}
            <div className="text-center mt-6 text-white text-sm">
              <p>©由Emvyax提供技術支援</p>
            </div>
          </div>
        </div>
      )}
    </LiffLoginGate>
  );
}
